import React from 'react';
import dayjs from 'dayjs-ext';
import relativeTime from 'dayjs-ext/plugin/relativeTime';

dayjs.extend(relativeTime);

export function formatCreatedAt(date, asElements = false) {
  const parsed = dayjs(date);
  if (asElements) {
    return (
      <>
        {parsed.format('DD-MM-YYYY')} <span>at</span> {parsed.format('HH:mm:ss')}
      </>
    );
  }
  return `${parsed.format('DD-MM-YYYY')} at ${parsed.format('HH:mm:ss')}`;
}

export function formatTradeDate(date) {
  return dayjs(date).format('HH:mm:ss');
}

export const roundToPrevTick = (time, baseTimeInterval) => {
  return baseTimeInterval * Math.floor(time / baseTimeInterval);
};

export const roundToNearestTick = (time, baseTimeInterval) => {
  return baseTimeInterval * Math.round(time / baseTimeInterval);
};

export const roundToNextTick = (time, baseTimeInterval) => {
  return baseTimeInterval * Math.ceil(time / baseTimeInterval);
};

/**
 * Chart data range can extend beyond the chart "viewport", this
 * returns the range of data that is initially visible to the user.
 * e.g. if 15min segments are selected, show the past 7.5 hours.
 * Any other data that falls outside the initial visible range will be
 * accessible to the user by scrolling horizontally on the chart.
 *
 * @param {string} frequency - time segment each candlestick represents
 * @returns {number} the visible range to show initially
 */
export const getChartRange = frequency =>
  ({
    Min: 1800000, // 60000 * 30  = 30 mins
    '5Min': 9000000, // 60000 * 60 * 2.5 = 2.5 hours
    '15Min': 27000000, // 60000 * 60 * 7.5 = 7.5 hours
    H: 108000000, // 60000 * 60 * 30 = 30 hours
    '6H': 648000000, // 60000 * 60 * 24 * 7.5 = 7.5 days
    D: 2592000000 // 60000 * 60 * 24 * 30 = 30 days
  }[frequency]);

export const getChartMinTickInterval = frequency =>
  // frequency in ms
  ({
    Min: 60000,
    '5Min': 300000,
    '15Min': 900000,
    H: 3600000,
    '6H': 21600000,
    D: 86400000
  }[frequency]);

/**
 * Returns the start date to fetch data from. Uses frequency
 * returned from getChartRange() and times it by 3.
 *
 * @param {number} frequency
 * @param {boolean} asDayJS
 * @returns
 */
export const getChartStartDate = (frequency, asDayJS) => {
  const now = roundToNextTick(dayjs(new Date()).valueOf(), getChartMinTickInterval(frequency));
  const start = dayjs(now - getChartRange(frequency) * 3);
  return asDayJS ? start : start.toISOString();
};

export function getIntervalInMinutes(interval) {
  switch (interval) {
    case 'D':
      return 1440;
    case '6H':
      return 360;
    case 'H':
      return 60;
    case '15Min':
      return 15;
    case '5Min':
      return 5;
    case 'Min':
      return 1;
    default:
      return 1440;
  }
}
